import { useRouter } from '@/routing/useAppRouter';
import { useTranslation } from 'next-i18next/pages';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { InquiryFormType } from './InquiryFormProvider';
import {
  parseInquiryCtaSource,
  solutionIdForInquiryCtaSource,
} from './inquiryCtaSource';
import {
  inquiryInterestLabelKey,
  parseInquiryInterestQuery,
} from './inquiryInterest';

/**
 * ### useInquiryQueryDefaults
 *
 * Pre-fills the inquiry form from the `?interest=` and `?source=` query parameters a
 * call-to-action attached to the link. Unrecognised values leave the form untouched.
 * The PoC message template is applied only when an industrial call-to-action asks for PoC terms,
 * so other interests start with an empty message.
 *
 * @param methods Form methods of the inquiry form to write defaults into
 */
export function useInquiryQueryDefaults(
  methods: UseFormReturn<InquiryFormType>,
): void {
  const router = useRouter();
  const { t } = useTranslation('common');
  const interestQuery = router.query.interest;
  const sourceQuery = router.query.source;

  useEffect(() => {
    if (!router.isReady) return;

    const interest = parseInquiryInterestQuery(interestQuery);
    const source = parseInquiryCtaSource(sourceQuery);

    if (interest) {
      methods.setValue(
        'interest',
        { value: interest, label: t(inquiryInterestLabelKey(interest)) },
        { shouldValidate: true },
      );
    }

    if (source) {
      methods.setValue('add_data.source', source);
    }

    if (
      interest === 'poc-terms' &&
      solutionIdForInquiryCtaSource(source) === 'listen_ai_industrial'
    ) {
      methods.setValue(
        'content',
        t(
          'Listen AI Industrial PoC 상담을 요청합니다. 양산 라인 품질검사, EOL 출하 전 최종 검사, 설비 예지보전 적용 가능성을 논의하고 싶습니다.',
        ),
        { shouldValidate: true },
      );
    }
  }, [interestQuery, methods, router.isReady, sourceQuery, t]);
}
